import { z } from "zod";
import { EServiceCategory } from "../constants/product.constants";

/**
 * Geo entity types for analytics
 */
export enum GeoEntityType {
  CITY = "city",
  SUB_CITY = "subCity",
  HUB = "hub",
  HOTSPOT = "hotspot",
}

/**
 * Geo entity query types (includes "all" option)
 */
export enum GeoEntityQueryType {
  CITY = "city",
  SUB_CITY = "subCity",
  HUB = "hub",
  HOTSPOT = "hotspot",
  ALL = "all",
}

/**
 * Visualization layers for geo analytics
 */
export enum VisualizationLayer {
  AVAILABILITY = "availability",
  WALKERS = "walkers",
  DEMAND = "demand",
  CITY_BOUNDARY = "city_boundary",
  SUBCITY_BOUNDARY = "subcity_boundary",
  HUB_BOUNDARY = "hub_boundary",
  ALL = "all",
}

/**
 * Metric types for color-coding geo entities
 */
export enum MetricType {
  AVAILABILITY_PERCENTAGE = "availabilityPercentage",
  WALKER_COUNT = "walkerCount",
  DEMAND_SCORE = "demandScore",
}

/**
 * Metrics that can be included in geo analytics calculations
 */
export enum IncludeMetric {
  WALKERS = "walkers",
  AVAILABILITY = "availability",
  DEMAND = "demand",
}

/**
 * Date presets for availability queries.
 * Backend converts these to actual date ranges.
 */
export enum DatePreset {
  TODAY = "TODAY",
  TOMORROW = "TOMORROW",
  NEXT_5_DAYS = "NEXT_5_DAYS",
  CUSTOM = "CUSTOM",
}

/**
 * Time presets for availability queries.
 * Backend converts these to slot indexes based on service category.
 */
export enum TimePreset {
  WORKING_HOURS = "WORKING_HOURS",
  PEAK_HOURS = "PEAK_HOURS",
  ULTRA_PEAK_HOURS = "ULTRA_PEAK_HOURS",
  CUSTOM = "CUSTOM",
}

/**
 * Availability state for geo entities.
 * Derived from availability percentage thresholds.
 * - NO_PARTNER: No partners attached (totalPartners === 0)
 * - LOW: Availability < 30%
 * - MEDIUM: Availability 30-70%
 * - HIGH: Availability > 70%
 */
export enum AvailabilityState {
  NO_PARTNER = "NO_PARTNER",
  LOW = "LOW",
  MEDIUM = "MEDIUM",
  HIGH = "HIGH",
}

/**
 * Partner state for geo entities.
 * Provides more granular information about partner availability.
 * - NOT_ATTACHED: No partners assigned to the geo entity
 * - ATTACHED_NOT_AVAILABLE: Partners assigned but none have working hours configured
 * - AVAILABLE_BUSY: Partners have working hours but all are busy/on leave
 * - AVAILABLE: At least one partner is available
 */
export enum PartnerState {
  NOT_ATTACHED = "NOT_ATTACHED",
  ATTACHED_NOT_AVAILABLE = "ATTACHED_NOT_AVAILABLE",
  AVAILABLE_BUSY = "AVAILABLE_BUSY",
  AVAILABLE = "AVAILABLE",
}


/**
 * GeoJSON geometry types
 */
export enum GeoJSONGeometryType {
  POLYGON = "Polygon",
  POINT = "Point",
}

/**
 * Date input structure (IST timezone)
 * Matches SlotDateInput from backend
 */
export interface IGeoDateInput {
  date: number; // 1-31
  month: number; // 1-12
  year: number; // four-digit year
}

/**
 * Time input structure (IST timezone)
 * Matches SlotTimeInput from backend
 */
export interface IGeoTimeInput {
  hour: number; // 0-23
  minute: number; // 0-59
}

/**
 * Date and time input structure (IST timezone)
 */
export interface IGeoDateTimeInput {
  date: IGeoDateInput;
  time: IGeoTimeInput;
}

/**
 * Custom time range with start and end date/time components in IST
 * Matches CustomTimeRange from backend
 */
export interface ICustomTimeRange {
  start: IGeoDateTimeInput;
  end: IGeoDateTimeInput;
}

/**
 * Bounds schema for geographic coordinates
 * Used in API responses and requests
 */
export const geoBoundsSchema = z.object({
  north: z.number(),
  south: z.number(),
  east: z.number(),
  west: z.number(),
});

/**
 * Bounds type for geographic coordinates
 * Inferred from Zod schema
 */
export type IGeoBounds = z.infer<typeof geoBoundsSchema>;

/**
 * Location codes schema for geo entities
 * Used in feature properties
 */
export const geoLocationCodesSchema = z.object({
  cityCode: z.string().nullish(),
  subCityCode: z.string().nullish(),
  hubCode: z.string().nullish(),
});

/**
 * Location codes type
 * Inferred from Zod schema
 */
export type IGeoLocationCodes = z.infer<typeof geoLocationCodesSchema>;

/**
 * Daily availability breakdown for multi-day calculations
 */
export const geoDailyAvailabilitySchema = z.object({
  date: z.string(), // YYYY-MM-DD format
  serviceableSlots: z.number(),
  totalPossibleSlots: z.number(),
  availabilityPercentage: z.number(),
});

export type IGeoDailyAvailability = z.infer<typeof geoDailyAvailabilitySchema>;

/**
 * Metrics schema for geo analytics
 * Used in feature properties
 *
 * New availability definition (N/D):
 * - serviceableSlots (N): Sum of available partners across all slots in time range
 * - totalPossibleSlots (D): Number of slots × partner count
 * - availabilityPercentage: N/D × 100
 */
export const geoMetricsSchema = z.object({
  // New availability metrics (N/D calculation)
  serviceableSlots: z.number().nullish(), // N: sum of available partners across slots
  totalPossibleSlots: z.number().nullish(), // D: number of slots × partner count
  availabilityPercentage: z.number().nullish(), // N/D × 100

  // Partner counts
  totalPartners: z.number().nullish(),
  partnersWithWorkingHours: z.number().nullish(),
  availablePartners: z.number().nullish(), // Max available partners across all slots

  // State differentiation
  availabilityState: z.nativeEnum(AvailabilityState).nullish(), // NO_PARTNER | LOW | MEDIUM | HIGH
  partnerState: z.nativeEnum(PartnerState).nullish(), // NOT_ATTACHED | ATTACHED_NOT_AVAILABLE | AVAILABLE_BUSY | AVAILABLE

  // Multi-day breakdown (only present for multi-day date presets)
  dailyAvailabilities: z.array(geoDailyAvailabilitySchema).nullish(),

  // Demand metrics
  demandScore: z.number().nullish(),
  subscriptionCount: z.number().nullish(),
});

/**
 * Metrics type
 * Inferred from Zod schema
 */
export type IGeoMetrics = z.infer<typeof geoMetricsSchema>;

/**
 * GeoJSON Feature Collection Response Schema
 */
export const geojsonFeatureCollectionResponseSchema = z.object({
  type: z.literal("FeatureCollection"),
  features: z.array(
    z.object({
      type: z.literal("Feature"),
      geometry: z.union([
        z.object({
          type: z.literal(GeoJSONGeometryType.POLYGON),
          coordinates: z.array(z.array(z.array(z.number()))),
        }),
        z.object({
          type: z.literal(GeoJSONGeometryType.POINT),
          coordinates: z.array(z.number()).length(2),
        }),
      ]),
      properties: geoLocationCodesSchema
        .merge(z.object({
          id: z.string(),
          code: z.string(),
          name: z.string(),
          type: z.nativeEnum(GeoEntityType),
          metrics: geoMetricsSchema,
          display: z.object({
            color: z.string(),
            opacity: z.number(),
            fillOpacity: z.number().optional(),
            strokeColor: z.string(),
            strokeWidth: z.number(),
            radius: z.number().nullish(),
            intensity: z.number().nullish(),
            label: z.string().nullish(),
            tooltip: z.string(),
          }),
        })),
    }),
  ),
  metadata: z.object({
    totalFeatures: z.number(),
    bounds: geoBoundsSchema.nullish(),
    layers: z.array(
      z.object({
        name: z.string(),
        enabled: z.boolean(),
        colorScale: z
          .object({
            high: z.object({ threshold: z.number(), color: z.string() }),
            medium: z.object({ threshold: z.number(), color: z.string() }),
            low: z.object({ threshold: z.number(), color: z.string() }),
          })
          .nullish(),
      }),
    ),
    heatmapDefaults: z
      .object({
        suggestedRadius: z.number(),
        suggestedBlur: z.number(),
        suggestedGradient: z.record(z.number(), z.string()),
        maxIntensity: z.number(),
      })
      .nullish(),
  }),
});

export type IGeoJSONFeatureCollectionResponse = z.infer<
  typeof geojsonFeatureCollectionResponseSchema
>;

/**
 * GeoJSON Query Request Schema
 */
export const geojsonQueryRequestSchema = z
  .object({
    type: z.nativeEnum(GeoEntityQueryType).default(GeoEntityQueryType.ALL),

    cityCode: z.string().optional(),

    subCityCode: z.string().optional(),

    hubCode: z.string().optional(),

    hotspotCode: z.string().optional(),

    layer: z.nativeEnum(VisualizationLayer).default(VisualizationLayer.AVAILABILITY),

    metric: z.nativeEnum(MetricType).default(MetricType.AVAILABILITY_PERCENTAGE),

    serviceCategory: z.nativeEnum(EServiceCategory).optional(),

    // New preset-based filters
    datePreset: z.nativeEnum(DatePreset).optional(),
    customDateRange: z
      .object({
        start: z.number().int().positive(),
        end: z.number().int().positive(),
      })
      .optional(),
    timePreset: z.nativeEnum(TimePreset).optional(),
    customSlotIndexes: z.array(z.number().int().min(0).max(47)).optional(),

    includeMetrics: z
      .string()
      .optional()
      .transform((val) => {
        if (!val) return undefined;
        return val.split(",").map((m) => m.trim());
      }),

    bounds: z
      .string()
      .optional()
      .transform((val) => {
        if (!val) return undefined;
        const parts = val.split(",").map(Number);
        if (parts.length !== 4 || parts.some(isNaN)) return undefined;
        return {
          north: parts[0],
          south: parts[1],
          east: parts[2],
          west: parts[3],
        };
      }),
  });

/**
 * Type for GeoJSON Query Request parameters
 * Use this when building query params to pass to the API
 * Note: bounds and includeMetrics can be passed as objects/arrays (for convenience) or strings (as zod expects)
 */
export type IGeoJSONQueryRequest = Omit<z.input<typeof geojsonQueryRequestSchema>, "bounds" | "includeMetrics"> & {
  bounds?: string | IGeoBounds;
  includeMetrics?: string | string[];
};

/**
 * Metric item for display in geo metrics components
 */
export interface IMetricItem {
  label: string;
  value: string | number;
  group?: string;
}
